import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress, Typography, Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

// Get the client ID from environment variables
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const MICROSOFT_TENANT_ID = 'f9f6158e-c0a3-4ab4-a41a-0556aa42adb7';
const API_BASE_URL = API_URL;

const AuthCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(3);
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  // Add countdown effect
  useEffect(() => {
    if (error && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [error, countdown]);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code from URL parameters
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        if (error) {
          throw new Error(errorDescription || error);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        // Exchange the code for a token (no PKCE - backend uses client_secret)
        const response = await fetch(`${API_BASE_URL}/auth/microsoft`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            client_id: MICROSOFT_CLIENT_ID,
            tenant_id: MICROSOFT_TENANT_ID
          }),
        });

        // Read the body as text first so an empty/non-JSON response
        // (e.g. a 500 or CORS-blocked reply) doesn't crash JSON.parse and
        // mask the real error.
        const rawBody = await response.text();
        const parseBody = (): any => {
          if (!rawBody) return null;
          try {
            return JSON.parse(rawBody);
          } catch {
            return null;
          }
        };

        if (!response.ok) {
          const errorData = parseBody();

          // Check if there's a redirect URL in the error response
          if (errorData?.redirect) {
            // Set the error message
            setError(errorData.error || 'Authentication failed');

            // Redirect to the specified URL after a short delay
            setTimeout(() => {
              window.location.href = errorData.redirect;
            }, 3000);

            return;
          }

          throw new Error(
            errorData?.error ||
              `Authentication failed (HTTP ${response.status} ${response.statusText})${
                rawBody ? `: ${rawBody.slice(0, 200)}` : ''
              }`
          );
        }

        const data = parseBody();
        if (!data) {
          throw new Error('Authentication succeeded but the server returned an empty response.');
        }
        console.log('Token response:', data);

        // Log the staff_no for verification
        console.log('User staff_no:', data.user?.staff_no);

        // Update auth context with full response (includes session_token)
        login(data);
        navigate('/');
      } catch (err) {
        console.error('Authentication error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    handleCallback();
  }, [location, navigate, login]);

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error" variant="h6" gutterBottom>
          {error}
        </Typography>
        {countdown > 0 && (
          <Typography variant="body2" color="text.secondary">
            Redirecting to login page in {countdown} seconds...
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export default AuthCallback; 